import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Home, Film } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
      const data = await response.json();

      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <Loader size={64} className="animate-spin text-red-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="bg-green-500/20 rounded-full p-6 inline-block mb-6">
              <CheckCircle size={64} className="text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-gray-400 mb-6">
              Thank you for your purchase. Your content is now available.
            </p>
            {paymentDetails && (
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                <p className="text-gray-400 text-sm">Amount Paid</p>
                <p className="text-2xl font-bold text-white mb-2">
                  ${paymentDetails.amount?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                </p>
                {paymentDetails.metadata?.content_title && (
                  <>
                    <p className="text-gray-400 text-sm">Content</p>
                    <p className="text-white">{paymentDetails.metadata.content_title}</p>
                  </>
                )}
                {paymentDetails.metadata?.plan_name && (
                  <>
                    <p className="text-gray-400 text-sm">Plan</p>
                    <p className="text-white">{paymentDetails.metadata.plan_name}</p>
                  </>
                )}
              </div>
            )}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg"
              >
                <Home size={20} />
                Go Home
              </button>
              <button
                onClick={() => navigate('/library')}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg"
              >
                <Film size={20} />
                Browse Content
              </button>
            </div>
          </div>
        );

      case 'expired':
      case 'error':
      case 'timeout':
        return (
          <div className="text-center">
            <div className="bg-red-500/20 rounded-full p-6 inline-block mb-6">
              <XCircle size={64} className="text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {status === 'expired' ? 'Payment Expired' : 'Payment Failed'}
            </h2>
            <p className="text-gray-400 mb-6">
              {status === 'timeout'
                ? 'Payment verification timed out. Please check your email for confirmation.'
                : 'Something went wrong with your payment. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg mx-auto"
            >
              <Home size={20} />
              Go Home
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl p-8 max-w-lg w-full border border-gray-800">
        {renderContent()}
      </div>
    </div>
  );
};

const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl p-8 max-w-lg w-full border border-gray-800 text-center">
        <div className="bg-yellow-500/20 rounded-full p-6 inline-block mb-6">
          <XCircle size={64} className="text-yellow-500" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">Payment Cancelled</h2>
        <p className="text-gray-400 mb-6">
          Your payment was cancelled. No charges have been made.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg"
          >
            Go Back
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg"
          >
            <Home size={20} />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export { PaymentSuccess, PaymentCancel };
